/**
 * CatalogueLinkBadges — inbound/outbound link count badges + reverse-pass opportunity dot.
 *
 * Reads from a context-cached getLinkGraph() response so 50 article rows
 * don't trigger 50 graph fetches. The cache is keyed by projectId and
 * invalidated whenever projectId changes.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { getLinkGraph } from '../../services/api';
import './CatalogueLinkBadges.css';

// ---------------------------------------------------------------------------
// Context for cached graph data
// ---------------------------------------------------------------------------

interface GraphNode {
    task_id: string;
    title: string;
    url: string;
    stream: string | null;
    inbound: number;
    outbound: number;
    orphan: boolean;
}

interface GraphData {
    nodes: GraphNode[];
    edges: any[];
}

interface LinkGraphCacheContextType {
    graphData: GraphData | null;
    loading: boolean;
    projectId: string | null;
    setProjectId: (id: string | null) => void;
    refresh: () => void;
}

const LinkGraphCacheContext = createContext<LinkGraphCacheContextType>({
    graphData: null,
    loading: false,
    projectId: null,
    setProjectId: () => {},
    refresh: () => {},
});

export const LinkGraphCacheProvider: React.FC<{ children: ReactNode; projectId: string | null }> = ({
    children,
    projectId,
}) => {
    const [graphData, setGraphData] = useState<GraphData | null>(null);
    const [loading, setLoading] = useState(false);
    const [currentProjectId, setCurrentProjectId] = useState<string | null>(projectId);

    const fetchGraph = useCallback(async (pid: string) => {
        setLoading(true);
        try {
            const data = await getLinkGraph(pid);
            setGraphData(data);
        } catch {
            setGraphData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setCurrentProjectId(projectId);
        if (projectId) {
            fetchGraph(projectId);
        } else {
            setGraphData(null);
        }
    }, [projectId, fetchGraph]);

    const refresh = useCallback(() => {
        if (currentProjectId) {
            fetchGraph(currentProjectId);
        }
    }, [currentProjectId, fetchGraph]);

    return (
        <LinkGraphCacheContext.Provider
            value={{
                graphData,
                loading,
                projectId: currentProjectId,
                setProjectId: setCurrentProjectId,
                refresh,
            }}
        >
            {children}
        </LinkGraphCacheContext.Provider>
    );
};

export const useLinkGraphCache = () => useContext(LinkGraphCacheContext);

// ---------------------------------------------------------------------------
// CatalogueLinkBadges component
// ---------------------------------------------------------------------------

interface CatalogueLinkBadgesProps {
    taskId: string;
    projectId: string;
    /** pending_link_opportunities from project.client_info (keyed by target_task_id) */
    pendingOpportunities?: Record<string, any[]>;
}

const CatalogueLinkBadges: React.FC<CatalogueLinkBadgesProps> = ({
    taskId,
    projectId,
    pendingOpportunities,
}) => {
    const { graphData } = useLinkGraphCache();

    const node = graphData?.nodes?.find(n => n.task_id === taskId);
    const inbound = node?.inbound ?? 0;
    const outbound = node?.outbound ?? 0;
    const hasPending = pendingOpportunities && (pendingOpportunities[taskId]?.length ?? 0) > 0;
    const pendingCount = pendingOpportunities?.[taskId]?.length ?? 0;

    if (!node && !hasPending) return null;

    return (
        <span className="catalogue-link-badges">
            {node && (
                <>
                    <span
                        className="clb-badge clb-inbound"
                        title={`${inbound} inbound link${inbound !== 1 ? 's' : ''}`}
                    >
                        <ArrowDownLeft size={10} />
                        {inbound}
                    </span>
                    <span
                        className="clb-badge clb-outbound"
                        title={`${outbound} outbound link${outbound !== 1 ? 's' : ''}`}
                    >
                        <ArrowUpRight size={10} />
                        {outbound}
                    </span>
                </>
            )}
            {hasPending && (
                <span
                    className="clb-badge clb-pending"
                    title={`${pendingCount} reverse-link suggestion${pendingCount !== 1 ? 's' : ''} — this article could be linked from existing content`}
                >
                    <span className="clb-pending-dot" />
                    {pendingCount}
                </span>
            )}
        </span>
    );
};

export default CatalogueLinkBadges;
